"use client";

import {
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT_MONO,
  SELECT,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  balancingDistance,
  balancingForce,
  netTorque,
  type TorqueTerm,
} from "@/lib/tool-kit/physics-mechanics/torque";
import {
  ANGLE,
  FORCE,
  fromBase,
  LENGTH,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

export interface TorqueRow {
  id: number;
  force: string;
  forceUnit: string;
  distance: string;
  distanceUnit: string;
  angle: string;
  angleUnit: string;
  direction: "ccw" | "cw";
}

export interface BalanceState {
  rows: TorqueRow[];
  balanceDistance: string;
  balanceDistanceUnit: string;
  balanceForce: string;
  balanceForceUnit: string;
}

export interface BalanceStrings {
  rowLabel: string;
  rowForce: string;
  rowDistance: string;
  rowAngle: string;
  rowDirection: string;
  directionCcw: string;
  directionCw: string;
  unitFor: string;
  addRow: string;
  removeRow: string;
  balanceHeading: string;
  balanceDistance: string;
  balanceForce: string;
  balanceHint: string;
  netTorque: string;
  netCcw: string;
  netCw: string;
  totalCcw: string;
  totalCw: string;
  balanced: string;
  tableHeading: string;
  columnTerm: string;
  columnForce: string;
  columnDistance: string;
  columnAngle: string;
  columnDirection: string;
  columnTorque: string;
  workingHeading: string;
  stepTerm: string;
  stepNet: string;
  stepBalanceForce: string;
  stepBalanceDistance: string;
  balanceNeed: string;
  balanceUnreadable: string;
  balanceNegative: string;
}

const MAX_ROWS = 6;
const RAD_TO_DEG = 180 / Math.PI;

export function BalanceInputs({
  state,
  onState,
  strings,
  nextId,
}: {
  state: BalanceState;
  onState: (state: BalanceState) => void;
  strings: BalanceStrings;
  nextId: () => number;
}) {
  const patch = (id: number, change: Partial<TorqueRow>) =>
    onState({
      ...state,
      rows: state.rows.map((row) =>
        row.id === id ? { ...row, ...change } : row
      ),
    });
  const unitClass = `${SELECT.replace("w-full", "")} w-24 shrink-0`;
  return (
    <div className="space-y-4">
      {state.rows.map((row, index) => {
        const n = index + 1;
        return (
          <fieldset
            className="grid gap-3 rounded-lg border border-border bg-card p-3 sm:grid-cols-2"
            key={row.id}
          >
            <legend className={`${HINT} px-1`}>
              {t(strings.rowLabel, { n })}
            </legend>
            <div className="space-y-1">
              <span aria-hidden="true" className={HINT}>
                {strings.columnForce}
              </span>
              <div className="flex gap-2">
                <input
                  aria-label={t(strings.rowForce, { n })}
                  className={`${INPUT_MONO} min-w-0 flex-1`}
                  inputMode="decimal"
                  onChange={(event) =>
                    patch(row.id, { force: event.target.value })
                  }
                  value={row.force}
                />
                <select
                  aria-label={t(strings.unitFor, {
                    name: t(strings.rowForce, { n }),
                  })}
                  className={unitClass}
                  onChange={(event) =>
                    patch(row.id, { forceUnit: event.target.value })
                  }
                  value={row.forceUnit}
                >
                  {unitOptions(FORCE).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <span aria-hidden="true" className={HINT}>
                {strings.columnDistance}
              </span>
              <div className="flex gap-2">
                <input
                  aria-label={t(strings.rowDistance, { n })}
                  className={`${INPUT_MONO} min-w-0 flex-1`}
                  inputMode="decimal"
                  onChange={(event) =>
                    patch(row.id, { distance: event.target.value })
                  }
                  value={row.distance}
                />
                <select
                  aria-label={t(strings.unitFor, {
                    name: t(strings.rowDistance, { n }),
                  })}
                  className={unitClass}
                  onChange={(event) =>
                    patch(row.id, { distanceUnit: event.target.value })
                  }
                  value={row.distanceUnit}
                >
                  {unitOptions(LENGTH).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <span aria-hidden="true" className={HINT}>
                {strings.columnAngle}
              </span>
              <div className="flex gap-2">
                <input
                  aria-label={t(strings.rowAngle, { n })}
                  className={`${INPUT_MONO} min-w-0 flex-1`}
                  inputMode="decimal"
                  onChange={(event) =>
                    patch(row.id, { angle: event.target.value })
                  }
                  value={row.angle}
                />
                <select
                  aria-label={t(strings.unitFor, {
                    name: t(strings.rowAngle, { n }),
                  })}
                  className={unitClass}
                  onChange={(event) =>
                    patch(row.id, { angleUnit: event.target.value })
                  }
                  value={row.angleUnit}
                >
                  {unitOptions(ANGLE).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <span aria-hidden="true" className={HINT}>
                {strings.columnDirection}
              </span>
              <select
                aria-label={t(strings.rowDirection, { n })}
                className={SELECT}
                onChange={(event) =>
                  patch(row.id, {
                    direction: event.target.value === "cw" ? "cw" : "ccw",
                  })
                }
                value={row.direction}
              >
                <option value="ccw">{strings.directionCcw}</option>
                <option value="cw">{strings.directionCw}</option>
              </select>
            </div>
            {state.rows.length > 1 ? (
              <div className="sm:col-span-2">
                <button
                  className={BUTTON_SMALL}
                  onClick={() =>
                    onState({
                      ...state,
                      rows: state.rows.filter((other) => other.id !== row.id),
                    })
                  }
                  type="button"
                >
                  {t(strings.removeRow, { n })}
                </button>
              </div>
            ) : null}
          </fieldset>
        );
      })}
      {state.rows.length < MAX_ROWS ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            const last = state.rows.at(-1);
            onState({
              ...state,
              rows: [
                ...state.rows,
                {
                  id: nextId(),
                  force: "",
                  forceUnit: last?.forceUnit ?? "N",
                  distance: "",
                  distanceUnit: last?.distanceUnit ?? "m",
                  angle: "90",
                  angleUnit: "deg",
                  direction: last?.direction === "ccw" ? "cw" : "ccw",
                },
              ],
            });
          }}
          type="button"
        >
          {strings.addRow}
        </button>
      ) : null}
      <p className={`${HINT} font-medium`}>{strings.balanceHeading}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          hint={strings.balanceHint}
          id="tq-balance-distance"
          label={strings.balanceDistance}
          onUnit={(balanceDistanceUnit) =>
            onState({ ...state, balanceDistanceUnit })
          }
          onValue={(balanceDistance) => onState({ ...state, balanceDistance })}
          unit={state.balanceDistanceUnit}
          unitLabel={t(strings.unitFor, { name: strings.balanceDistance })}
          units={unitOptions(LENGTH)}
          value={state.balanceDistance}
        />
        <QuantityField
          id="tq-balance-force"
          label={strings.balanceForce}
          onUnit={(balanceForceUnit) => onState({ ...state, balanceForceUnit })}
          onValue={(balanceForce) => onState({ ...state, balanceForce })}
          unit={state.balanceForceUnit}
          unitLabel={t(strings.unitFor, { name: strings.balanceForce })}
          units={unitOptions(FORCE)}
          value={state.balanceForce}
        />
      </div>
    </div>
  );
}

type Read =
  | { kind: "ok"; terms: TorqueTerm[] }
  | { kind: "need" }
  | { kind: "unreadable"; n: number }
  | { kind: "negative"; n: number };

function readRows(rows: readonly TorqueRow[]): Read {
  const terms: TorqueTerm[] = [];
  for (const [index, row] of rows.entries()) {
    if ([row.force, row.distance].every((text) => text.trim() === "")) {
      continue;
    }
    const force = parseQuantity(row.force);
    const distance = parseQuantity(row.distance);
    const angle =
      row.angle.trim() === ""
        ? Math.PI / 2 / toBase(1, ANGLE, row.angleUnit)
        : parseQuantity(row.angle);
    if (force === null || distance === null || angle === null) {
      return { kind: "unreadable", n: index + 1 };
    }
    if (force < 0 || distance < 0) {
      return { kind: "negative", n: index + 1 };
    }
    terms.push({
      force: toBase(force, FORCE, row.forceUnit),
      distance: toBase(distance, LENGTH, row.distanceUnit),
      angle: toBase(angle, ANGLE, row.angleUnit),
      direction: row.direction,
    });
  }
  return terms.length === 0 ? { kind: "need" } : { kind: "ok", terms };
}

function stepsFor(
  terms: readonly TorqueTerm[],
  torques: readonly number[],
  total: { cw: number; ccw: number; net: number },
  strings: BalanceStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const p = (value: number) => formatPlain(fmt, value);
  const r = (value: number) => formatSig(fmt, value, sig);
  const steps = terms.map((term, index) =>
    t(strings.stepTerm, {
      n: index + 1,
      distance: p(term.distance),
      force: p(term.force),
      angle: p(term.angle * RAD_TO_DEG),
      torque: r(Math.abs(torques[index])),
      direction:
        term.direction === "ccw" ? strings.directionCcw : strings.directionCw,
    })
  );
  steps.push(
    t(strings.stepNet, {
      ccw: r(total.ccw),
      cw: r(total.cw),
      net: r(Math.abs(total.net)),
    })
  );
  return steps;
}

export function BalanceResults({
  state,
  strings,
  fmt,
  sig,
}: {
  state: BalanceState;
  strings: BalanceStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const read = readRows(state.rows);
  if (read.kind === "need") {
    return <Notice>{strings.balanceNeed}</Notice>;
  }
  if (read.kind === "unreadable") {
    return (
      <ErrorNotice>{t(strings.balanceUnreadable, { n: read.n })}</ErrorNotice>
    );
  }
  if (read.kind === "negative") {
    return (
      <ErrorNotice>{t(strings.balanceNegative, { n: read.n })}</ErrorNotice>
    );
  }
  const result = netTorque(read.terms);
  const nm = (value: number) => withUnit(formatSig(fmt, value, sig), "N·m");
  const netText =
    result.net === 0
      ? nm(0)
      : `${nm(Math.abs(result.net))} ${result.net > 0 ? strings.netCcw : strings.netCw}`;
  const distance =
    state.balanceDistance.trim() === ""
      ? null
      : parseQuantity(state.balanceDistance);
  const force =
    state.balanceForce.trim() === "" ? null : parseQuantity(state.balanceForce);
  const extra: string[] = [];
  const needed = result.net > 0 ? strings.directionCw : strings.directionCcw;
  if (!result.balanced && distance !== null) {
    const si = toBase(distance, LENGTH, state.balanceDistanceUnit);
    const value = balancingForce(result.net, si);
    if (value !== null) {
      extra.push(
        t(strings.stepBalanceForce, {
          torque: formatSig(fmt, Math.abs(result.net), sig),
          distance: formatPlain(fmt, si),
          force: withUnit(
            formatSig(fmt, fromBase(value, FORCE, state.balanceForceUnit), sig),
            unitSymbol(FORCE, state.balanceForceUnit)
          ),
          direction: needed,
        })
      );
    }
  }
  if (!result.balanced && force !== null) {
    const si = toBase(force, FORCE, state.balanceForceUnit);
    const value = balancingDistance(result.net, si);
    if (value !== null) {
      extra.push(
        t(strings.stepBalanceDistance, {
          torque: formatSig(fmt, Math.abs(result.net), sig),
          force: formatPlain(fmt, si),
          distance: withUnit(
            formatSig(
              fmt,
              fromBase(value, LENGTH, state.balanceDistanceUnit),
              sig
            ),
            unitSymbol(LENGTH, state.balanceDistanceUnit)
          ),
          direction: needed,
        })
      );
    }
  }
  return (
    <>
      {result.balanced ? <Notice>{strings.balanced}</Notice> : null}
      <StatGrid>
        <StatCard emphasis label={strings.netTorque} value={netText} />
        <StatCard
          label={strings.totalCcw}
          value={nm(result.counterclockwise)}
        />
        <StatCard label={strings.totalCw} value={nm(result.clockwise)} />
      </StatGrid>
      <Working
        heading={strings.workingHeading}
        steps={[
          ...stepsFor(
            read.terms,
            result.torques,
            {
              cw: result.clockwise,
              ccw: result.counterclockwise,
              net: result.net,
            },
            strings,
            fmt,
            sig
          ),
          ...extra,
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.columnTerm,
          `${strings.columnForce} (N)`,
          `${strings.columnDistance} (m)`,
          `${strings.columnAngle} (°)`,
          strings.columnDirection,
          `${strings.columnTorque} (N·m)`,
        ]}
        rows={read.terms.map((term, index) => [
          t(strings.rowLabel, { n: index + 1 }),
          formatSig(fmt, term.force, sig),
          formatSig(fmt, term.distance, sig),
          formatSig(fmt, term.angle * RAD_TO_DEG, sig),
          term.direction === "ccw" ? strings.directionCcw : strings.directionCw,
          formatSig(fmt, Math.abs(result.torques[index]), sig),
        ])}
      />
    </>
  );
}
